import { Alert, AlertDescription, AlertTitle } from "design-system/atoms/Alert"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import DateDisplay from "design-system/molecules/DateDisplay"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { UploadInput } from "design-system/organisms/UploadInput"
import { cn } from "design-system/utils"
import { AlertTriangle, Copy, Download, Eye, History, Info, RefreshCcw, Trash2 } from "lucide-react"
import { DraftItem } from "modules/knowledge/hooks/useKnowledgeBaseForm"
import { ChangeLogItem } from "modules/shared/components/ChangeLogItem"
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
} from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  getKnowledgeBaseWebsiteRefreshDelayMs,
  KNOWLEDGE_BASE_FORMAT,
  KNOWLEDGE_BASE_FORMAT_COPIES,
  KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES,
  KNOWLEDGE_BASE_MAX_FILE_SIZE_MB,
  KnowledgeBaseItemKind,
} from "schema"
import { toast } from "sonner"
import { KnowledgeBaseCampaignInfo } from "./CampaignInfo"

interface KnowledgeBaseItemFormProps {
  id: string
  item: DraftItem
  onRemove: () => void
  isDisabled?: boolean
  onUpdateField: (updates: Partial<DraftItem>) => void
  onSave: (updates?: Partial<DraftItem>) => Promise<void>
  onDuplicate: () => void
  campaignId?: string | null
  kind: KnowledgeBaseItemKind
  onRetrieveWebpage: (id: string) => void
  autoFocus?: boolean
  highlightId?: string
}

type KnowledgeBaseItemFormRef = {
  focus: () => void
}

type EditableField = "title" | "content"

type DocumentWithCaret = Document & {
  caretPositionFromPoint?: (
    x: number,
    y: number,
  ) => {
    offset: number
  } | null
  caretRangeFromPoint?: (x: number, y: number) => Range | null
}

type ScrollSnapshot = {
  element: Element | Window
  left: number
  top: number
}

function restoreScrollSnapshot(snapshot: ScrollSnapshot[] | null) {
  if (!snapshot) return

  snapshot.forEach(({ element, left, top }) => {
    if (element instanceof Window) {
      element.scrollTo(left, top)
      return
    }

    element.scrollLeft = left
    element.scrollTop = top
  })
}

function getScrollSnapshot(target: HTMLElement) {
  const snapshot: ScrollSnapshot[] = [
    {
      element: window,
      left: window.scrollX,
      top: window.scrollY,
    },
  ]
  let element = target.parentElement

  while (element) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(element)
    const isScrollable =
      /(auto|scroll|overlay)/.test(`${overflow}${overflowX}${overflowY}`) &&
      (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth)

    if (isScrollable) {
      snapshot.push({
        element,
        left: element.scrollLeft,
        top: element.scrollTop,
      })
    }

    element = element.parentElement
  }

  return snapshot
}

export const KnowledgeBaseItemForm = forwardRef<
  KnowledgeBaseItemFormRef,
  KnowledgeBaseItemFormProps
>(
  (
    {
      item,
      onRemove,
      isDisabled,
      onUpdateField,
      onSave,
      onDuplicate,
      onRetrieveWebpage,
      campaignId,
      kind,
      autoFocus = false,
      highlightId,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)
    const isSavingFromEnterRef = useRef(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const pendingSelectionRef = useRef<number | null>(null)
    const pendingScrollSnapshotRef = useRef<ScrollSnapshot[] | null>(null)
    const [isHighlighted, setIsHighlighted] = useState(false)
    const [editingField, setEditingField] = useState<EditableField | null>(null)

    function startEditing(field: EditableField, selectionStart?: number) {
      if (isDisabled) return

      pendingSelectionRef.current = selectionStart ?? null
      setEditingField(field)
    }

    function handleEditablePointerDown(event: PointerEvent<HTMLButtonElement>) {
      pendingScrollSnapshotRef.current = getScrollSnapshot(event.currentTarget)
    }

    function getClickedTextOffset(event: MouseEvent<HTMLElement>, value: string) {
      if (!value) return 0

      const ownerDocument = event.currentTarget.ownerDocument
      const maxOffset = value.length
      const position = (ownerDocument as DocumentWithCaret).caretPositionFromPoint?.(
        event.clientX,
        event.clientY,
      )

      if (position) {
        return Math.min(position.offset, maxOffset)
      }

      const range = (ownerDocument as DocumentWithCaret).caretRangeFromPoint?.(
        event.clientX,
        event.clientY,
      )

      return Math.min(range?.startOffset ?? maxOffset, maxOffset)
    }

    useImperativeHandle(ref, () => ({
      focus: () => {
        const { kind: itemKind } = item

        if (itemKind === "Text") {
          setEditingField("content")
        } else if (itemKind === "QuestionAndAnswer" || itemKind === "Website") {
          setEditingField("title")
        }

        setTimeout(() => {
          if (itemKind === "Text") {
            textareaRef.current?.focus({ preventScroll: true })
          } else {
            inputRef.current?.focus({ preventScroll: true })
          }
        }, 0)
      },
    }))

    const {
      id: itemId,
      hasChangeLog,
      title: itemTitle,
      isRetrieving,
      retrievedAt,
      fetchFailedAt,
      websiteRefreshFrequency,
      kind: itemKind,
      campaignId: campaignIdField,
    } = item
    const isPersisted = !!itemId && !itemId.startsWith("draft-")
    const isFile = itemKind === "File"
    const isWebsite = itemKind === "Website"

    function handleRequestDelete() {
      if (isPersisted) {
        setIsDeleteDialogOpen(true)
      } else {
        onRemove()
      }
    }

    async function handleDownloadFile() {
      const url = item.content
      if (typeof url === "string") {
        const response = await fetch(url)
        const blob = await response.blob()
        const fileUrl = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = fileUrl
        link.setAttribute("download", itemTitle || "")
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(fileUrl)
      }
    }

    function renderEditableText({
      field,
      value,
      placeholder,
      multiline = false,
      className,
      avoidFieldStyles,
    }: {
      field: EditableField
      value: string
      placeholder: string
      multiline?: boolean
      className?: string
      avoidFieldStyles?: boolean
    }) {
      return (
        <button
          type="button"
          disabled={isDisabled}
          onPointerDown={handleEditablePointerDown}
          onClick={(event) => startEditing(field, getClickedTextOffset(event, value))}
          className={cn(
            "w-full bg-background text-left text-sm transition-colors flex align-top whitespace-pre-wrap",
            !avoidFieldStyles && "border border-input rounded-md p-2 px-3",
            !value && "text-muted-foreground",
            multiline && "min-h-20",
            className,
          )}
        >
          {value || placeholder}
        </button>
      )
    }

    const field = (() => {
      switch (itemKind) {
        case "Text":
          return (
            <div className="flex-1">
              {editingField === "content" ? (
                <Textarea
                  ref={textareaRef}
                  value={(item.content as string) ?? ""}
                  className="min-h-40 p-3 pl-14 rounded-lg"
                  style={{ overflow: "hidden" }}
                  placeholder={t("knowledge.form.entryPlaceholder")}
                  onBlur={() => {
                    setEditingField(null)
                    onSave()
                  }}
                  onChange={(e) => {
                    pendingScrollSnapshotRef.current = getScrollSnapshot(e.currentTarget)
                    onUpdateField({ content: e.target.value })
                  }}
                  disabled={isDisabled}
                />
              ) : (
                <Card className="min-h-40 p-3 pl-14 rounded-lg shadow-none">
                  {renderEditableText({
                    field: "content",
                    value: (item.content as string) ?? "",
                    placeholder: t("knowledge.form.entryPlaceholder"),
                    multiline: true,
                    className: "min-h-32",
                    avoidFieldStyles: true,
                  })}
                </Card>
              )}
            </div>
          )

        case "QuestionAndAnswer":
          return (
            <Card className="flex-1 min-h-40 p-4 pl-16 flex flex-col gap-4 shadow-none rounded-lg">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label>{t("knowledge.form.questionAndAnswer.question")}</Label>
                {editingField === "title" ? (
                  <Input
                    ref={inputRef}
                    value={itemTitle ?? ""}
                    placeholder={t("knowledge.form.questionAndAnswer.questionPlaceholder")}
                    onChange={(e) => {
                      pendingScrollSnapshotRef.current = getScrollSnapshot(e.currentTarget)
                      onUpdateField({ title: e.target.value })
                    }}
                    onBlur={() => {
                      setEditingField(null)
                      onSave()
                    }}
                    disabled={isDisabled}
                  />
                ) : (
                  renderEditableText({
                    field: "title",
                    value: itemTitle ?? "",
                    placeholder: t("knowledge.form.questionAndAnswer.questionPlaceholder"),
                  })
                )}
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <Label>{t("knowledge.form.questionAndAnswer.answer")}</Label>
                {editingField === "content" ? (
                  <Textarea
                    ref={textareaRef}
                    value={(item.content as string) ?? ""}
                    style={{ overflow: "hidden" }}
                    placeholder={t("knowledge.form.questionAndAnswer.answerPlaceholder")}
                    onBlur={() => {
                      setEditingField(null)
                      onSave()
                    }}
                    onChange={(e) => {
                      pendingScrollSnapshotRef.current = getScrollSnapshot(e.currentTarget)
                      onUpdateField({ content: e.target.value })
                    }}
                    disabled={isDisabled}
                  />
                ) : (
                  renderEditableText({
                    field: "content",
                    value: (item.content as string) ?? "",
                    placeholder: t("knowledge.form.questionAndAnswer.answerPlaceholder"),
                    multiline: true,
                  })
                )}
              </div>
            </Card>
          )

        case "File":
          function handleFilesChange(files: FileList) {
            if (files && files.length > 0) {
              const file = files[0]
              if (!file) return

              if (file.size > KNOWLEDGE_BASE_MAX_FILE_SIZE_BYTES) {
                toast.error(
                  t("knowledge.form.fileSizeError", {
                    fileName: file.name,
                    maxSize: `${KNOWLEDGE_BASE_MAX_FILE_SIZE_MB} MB`,
                  }),
                )

                return
              }

              const allowedMimeTypes = Object.values(KNOWLEDGE_BASE_FORMAT) as string[]
              if (!file?.type || !allowedMimeTypes.includes(file.type)) {
                toast.error(
                  t("knowledge.form.fileFormatError", {
                    fileName: file.name,
                    formats: Object.values(KNOWLEDGE_BASE_FORMAT_COPIES).join(", "),
                  }),
                )

                return
              }

              const updates = { content: files, title: files[0]?.name ?? null }
              onUpdateField(updates)

              // Use setTimeout to ensure state update happens first
              setTimeout(() => {
                onSave(updates)
              }, 0)
            }
          }

          const filePlaceholder = (
            <div className="flex flex-col items-center gap-2">
              <span className="break-words text-sm px-6 text-placeholder">
                {t("knowledge.form.filePlaceholder", {
                  maxSizeInMB: KNOWLEDGE_BASE_MAX_FILE_SIZE_MB,
                })}
              </span>
              <div className="flex items-center gap-1.5 opacity-75">
                {Object.values(KNOWLEDGE_BASE_FORMAT).map((format) => (
                  <Badge key={format} variant="outline">
                    {KNOWLEDGE_BASE_FORMAT_COPIES[format]}
                  </Badge>
                ))}
              </div>
            </div>
          )

          return (
            <div className="flex-1 flex flex-col gap-1.5">
              <UploadInput
                disabled={isDisabled || isPersisted}
                fileName={itemTitle ?? undefined}
                placeholder={filePlaceholder}
                onChange={(e) => {
                  const input = e.target
                  const files = input?.files
                  if (files) handleFilesChange(files)
                }}
                hideRemoveButton
                className="py-7 min-h-36 items-center justify-center"
                onDragEnter={(e) => e.stopPropagation()}
                onDragLeave={(e) => e.stopPropagation()}
                onDragOver={(e) => e.stopPropagation()}
                accept={Object.values(KNOWLEDGE_BASE_FORMAT).join(",")}
              />
            </div>
          )

        case "Website":
          const content = item.content as string | null

          return (
            <Card className="w-full p-4 pl-16 min-h-40 max-h-full flex flex-col gap-3 items-start overflow-hidden rounded-lg border shadow-none">
              <div className="w-full flex flex-col gap-1.5">
                <Label>{t("knowledge.form.website.label")}</Label>
                {editingField === "title" ? (
                  <Input
                    ref={inputRef}
                    type="url"
                    value={itemTitle ?? ""}
                    placeholder={t("knowledge.form.website.placeholder")}
                    disabled={isDisabled}
                    onChange={(e) => {
                      pendingScrollSnapshotRef.current = getScrollSnapshot(e.currentTarget)
                      onUpdateField({ title: e.target.value })
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        isSavingFromEnterRef.current = true
                        e.currentTarget.blur()
                        onSave().finally(() => {
                          // Reset flag after a small delay to allow blur event to complete
                          setTimeout(() => {
                            isSavingFromEnterRef.current = false
                          }, 0)
                        })
                      }
                    }}
                    onBlur={() => {
                      setEditingField(null)
                      // Skip save if it was already triggered by Enter key
                      if (!isSavingFromEnterRef.current) {
                        onSave()
                      }
                    }}
                  />
                ) : (
                  renderEditableText({
                    field: "title",
                    value: itemTitle ?? "",
                    placeholder: t("knowledge.form.website.placeholder"),
                    className: "break-all",
                  })
                )}
              </div>
              {fetchFailedAt && (
                <Alert variant="warning" className="w-full">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{t("knowledge.form.website.fetchFailed.title")}</AlertTitle>
                  <AlertDescription className="flex flex-col gap-1.5">
                    <span>{t("knowledge.form.website.fetchFailed.description")}</span>
                    <span className="text-xs text-muted-foreground">
                      <Trans
                        i18nKey="knowledge.form.website.fetchFailed.failedAt"
                        components={{ date: <DateDisplay date={fetchFailedAt.toISOString()} /> }}
                      />
                    </span>
                  </AlertDescription>
                </Alert>
              )}
              <div className="w-full flex lg:flex-row flex-col lg:justify-end justify-start lg:gap-2 gap-1 lg:items-center items-start">
                {retrievedAt && (
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">
                      <Trans
                        i18nKey="knowledge.form.website.lastRetrieved"
                        components={{ date: <DateDisplay date={retrievedAt.toISOString()} /> }}
                      />
                    </span>
                    {websiteRefreshFrequency && !fetchFailedAt && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="link"
                            className="p-0 w-auto"
                            disabled={!isPersisted || !!isRetrieving}
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <Trans
                            i18nKey="knowledge.form.website.tooltipRetrieve"
                            values={{
                              frequency: t(
                                `knowledge.form.website.refreshFrequency.${websiteRefreshFrequency}`,
                              ),
                            }}
                            components={{
                              date: (
                                <DateDisplay
                                  date={new Date(
                                    retrievedAt.getTime() +
                                      getKnowledgeBaseWebsiteRefreshDelayMs(
                                        websiteRefreshFrequency,
                                      ),
                                  ).toISOString()}
                                  onlyDate
                                />
                              ),
                            }}
                          />
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {content && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!isPersisted || !!isRetrieving}
                        >
                          <Eye className="h-4 w-4" />
                          <span>{t("knowledge.form.website.viewContent")}</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent size="4xl">
                        <DialogHeader>
                          <DialogTitle className="text-lg">
                            <Trans
                              i18nKey="knowledge.form.website.content"
                              components={{
                                link: (
                                  <a
                                    href={itemTitle || undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline hover:opacity-80"
                                  >
                                    {itemTitle}
                                  </a>
                                ),
                              }}
                            />
                          </DialogTitle>
                        </DialogHeader>
                        <MarkdownRenderer className="max-w-full overflow-auto">{`\`\`\`markdown\n${content}\n\`\`\``}</MarkdownRenderer>
                      </DialogContent>
                    </Dialog>
                  )}
                  {itemId && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!isPersisted || !!isRetrieving}
                      onClick={() => onRetrieveWebpage(itemId)}
                    >
                      <RefreshCcw className={cn("h-4 w-4", isRetrieving && "animate-spin")} />
                      <span>
                        {isRetrieving
                          ? t("knowledge.form.website.retrieving")
                          : t("knowledge.form.website.refresh")}
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )
      }
    })()

    useEffect(() => {
      if (itemId === highlightId) {
        setIsHighlighted(true)
        setTimeout(() => {
          setIsHighlighted(false)
        }, 3000)
      }
    }, [itemId, highlightId])

    useEffect(() => {
      if (!autoFocus) return

      if (itemKind === "Text") {
        setEditingField("content")
      } else if (itemKind === "QuestionAndAnswer" || itemKind === "Website") {
        setEditingField("title")
      }
    }, [autoFocus, itemKind])

    useLayoutEffect(() => {
      restoreScrollSnapshot(pendingScrollSnapshotRef.current)
    })

    useLayoutEffect(() => {
      if (!editingField) return

      const element = editingField === "content" ? textareaRef.current : inputRef.current
      if (!element) return

      element.focus({ preventScroll: true })

      const selectionStart = pendingSelectionRef.current
      if (selectionStart === null) return

      element.setSelectionRange(selectionStart, selectionStart)
      pendingSelectionRef.current = null
      restoreScrollSnapshot(pendingScrollSnapshotRef.current)

      requestAnimationFrame(() => {
        restoreScrollSnapshot(pendingScrollSnapshotRef.current)
        requestAnimationFrame(() => {
          restoreScrollSnapshot(pendingScrollSnapshotRef.current)
          pendingScrollSnapshotRef.current = null
        })
      })
    }, [editingField])

    return (
      <div
        className={cn(
          "relative rounded-lg ring-0 duration-300",
          isHighlighted && "ring-3 ring-warning",
        )}
        id={`item-${itemId}`}
      >
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("knowledge.form.deleteEntry")}</DialogTitle>
              <DialogDescription>{t("knowledge.form.deleteEntryConfirmation")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t("shared.cancel")}
                </Button>
              </DialogClose>
              <DialogClose asChild>
                <Button type="button" onClick={onRemove}>
                  {t("knowledge.form.deleteEntry")}
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {isHistoryDialogOpen && itemId && (
          <ChangeLogItem
            open={isHistoryDialogOpen}
            onOpenChange={setIsHistoryDialogOpen}
            model="KnowledgeBaseItem"
            recordId={itemId}
            title={itemTitle ?? undefined}
            fieldsMap={
              kind === "QuestionAndAnswer"
                ? [
                    { field: "title", label: t("knowledge.form.questionAndAnswer.question") },
                    { field: "content", label: t("knowledge.form.questionAndAnswer.answer") },
                  ]
                : [{ field: "content", label: t("knowledge.form.text.content") }]
            }
            canApplyChanges={(values) => {
              if (kind === "QuestionAndAnswer") {
                return values.title !== itemTitle || values.content !== item.content
              }
              return values.content !== item.content
            }}
            onApplyChanges={async (values) => {
              await onSave({
                content: values.content,
                title: values.title,
              })
            }}
          />
        )}
        <div className="flex gap-2 items-start">
          {field}
          <div className="flex flex-col absolute left-px top-px bottom-px p-1 border-r bg-background z-10 rounded-tl-lg rounded-bl-lg">
            {!isFile && !isWebsite && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isDisabled}
                    onClick={onDuplicate}
                    title={t("knowledge.form.duplicateEntry")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{t("knowledge.form.duplicateEntry")}</TooltipContent>
              </Tooltip>
            )}
            {isFile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      toast.promise(handleDownloadFile(), {
                        loading: t("knowledge.form.downloadFile.loading"),
                        success: t("knowledge.form.downloadFile.success"),
                        error: t("knowledge.form.downloadFile.error"),
                      })
                    }}
                    title={t("knowledge.form.downloadFile.label")}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t("knowledge.form.downloadFile.label")}
                </TooltipContent>
              </Tooltip>
            )}
            {!isFile && (
              <Tooltip>
                <TooltipTrigger asChild={hasChangeLog}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={!hasChangeLog}
                    onClick={() => setIsHistoryDialogOpen(true)}
                    title={
                      hasChangeLog
                        ? t("knowledge.form.viewHistory")
                        : t("shared.changeHistory.noChanges")
                    }
                  >
                    <History className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {hasChangeLog
                    ? t("knowledge.form.viewHistory")
                    : t("shared.changeHistory.noChanges")}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={isDisabled}
                  onClick={handleRequestDelete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{t("knowledge.form.deleteEntry")}</TooltipContent>
            </Tooltip>
            {campaignIdField && !campaignId ? (
              <KnowledgeBaseCampaignInfo campaignId={campaignIdField} />
            ) : null}
          </div>
        </div>
      </div>
    )
  },
)

KnowledgeBaseItemForm.displayName = "KnowledgeBaseItemForm"
